import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import ClassSelection from './ClassSelection';
import AttendanceTaking from './AttendanceTaking';
import AttendanceDashboard from './AttendanceDashboard';

const Attendance = () => {
  const { user, isTeacher, isAdmin } = useAuth();
  const { classes, getClassesForTeacher, markAttendance } = useData();
  // Default to dashboard
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Get classes from context
  const availableClasses = classes;

  const handleClassSelect = (classData, date, section = null) => {
    setSelectedClass(classData);
    setSelectedSection(section);
    setSelectedDate(date || new Date().toISOString().split('T')[0]);
    setCurrentPage('attendance-taking');
  };

  const handleBackToClasses = () => {
    setCurrentPage('class-selection');
    setSelectedClass(null);
    setSelectedSection(null);
  };

  const handleBackToDashboard = () => {
    setCurrentPage('dashboard');
    setSelectedClass(null);
    setSelectedSection(null);
  };

  const handleSubmitAttendance = async (attendanceData, absentStudents) => {
    try {
      // ... usage of markAttendance ...
      const payload = {
        unitId: selectedClass._id,
        date: selectedDate,
        attendanceData,
        absentStudents
      };

      if (selectedSection) {
        payload.sectionId = selectedSection._id;
      }
      
      const result = await markAttendance(payload);

      if (result.success) {
        if (absentStudents.length > 0) {
          await sendWhatsAppNotifications(absentStudents);
        }
        toast.success(`Attendance submitted for ${selectedClass.unitName}${selectedSection ? ` (${selectedSection.name || selectedSection.sectionName})` : ''} on ${selectedDate}`);
        handleBackToClasses();
      } else {
        const message = result?.message || result?.error || 'Failed to submit attendance';
        toast.error(message);
      }
    } catch (error) {
      const message = error.message || 'Error submitting attendance. Please try again.';
      toast.error(message);
    }
  };

  const sendWhatsAppNotifications = async (absentStudents) => {
     // ... no changes to this function ...
    const notifications = [];
    absentStudents.forEach(student => {
      if (student.parentPhone) {
        notifications.push({
          studentName: student.studentName,
          studentRegistrationId: student.studentRegistrationId,
          parentPhone: student.parentPhone,
          className: selectedClass.unitName,
          date: selectedDate,
          message: `Dear Parent, your child ${student.studentName} (ID: ${student.studentRegistrationId}) was marked absent in ${selectedClass.unitName} on ${selectedDate}. Please provide a reason for the absence.`
        });
      }
    });
    return notifications;
  };

  if (!isTeacher && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only teachers and administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Navigation Tabs (Only show if not taking attendance) */}
        {currentPage !== 'attendance-taking' && (
            <div className="bg-white px-8 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex bg-gray-100/50 p-1.5 rounded-2xl border border-gray-100/50">
                    <button 
                        onClick={() => setCurrentPage('dashboard')}
                        className={`px-8 py-2 text-sm font-bold rounded-[14px] transition-all duration-300 ${
                          currentPage === 'dashboard' 
                          ? 'bg-white text-gray-900 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                        style={currentPage === 'dashboard' ? { color: 'var(--primary-color, #4f46e5)' } : {}}
                    >
                        Attendance Dashboard
                    </button>
                    <button 
                        onClick={() => setCurrentPage('class-selection')}
                        className={`px-8 py-2 text-sm font-bold rounded-[14px] transition-all duration-300 ${
                          currentPage === 'class-selection' 
                          ? 'bg-white text-gray-900 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                        style={currentPage === 'class-selection' ? { color: 'var(--primary-color, #4f46e5)' } : {}}
                    >
                        Mark Attendance
                    </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className="px-3 py-1.5 bg-green-50 rounded-full border border-green-100 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-bold text-green-700 uppercase tracking-wider">Live System Active</span>
                  </div>
                </div>
            </div>
        )}

      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {currentPage === 'dashboard' && (
            <AttendanceDashboard 
            availableClasses={availableClasses}
            onClassSelect={handleClassSelect}
            onTakeAttendance={() => setCurrentPage('class-selection')}
            />
        )}
        
        {currentPage === 'class-selection' && (
            <ClassSelection 
            onClassSelect={handleClassSelect} 
            availableClasses={availableClasses}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            />
        )}
        
        {currentPage === 'attendance-taking' && selectedClass && (
            <AttendanceTaking 
            selectedClass={selectedClass}
            selectedSection={selectedSection}
            selectedDate={selectedDate}
            onBack={handleBackToClasses}
            onSubmitAttendance={handleSubmitAttendance}
            />
        )}
      </div>
    </div>
  );
};

export default Attendance;